import { useEffect } from 'react'
import { Icon } from '@iconify/react'
import { apm } from '@elastic/apm-rum'
import { Button, TabPane, Tabs, useGetReport } from '@pepper/ui'
import type { Topic, TopicKeyword } from '@pepper/utils/src/types/seo'

import CreateItemDropdown from 'components/CreateItemDropdown'
import { useSelectedCompanyId } from 'utils/functions/misc'
import ContentGuidelines from './ContentGuidelines/ContentGuidelines'
import Outline from './Outline/Outline'
import FAQs from './FAQs/FAQs'
import { tabs } from './CRRDrawer.utils'
import IdeaLabDrawer from '../../IdeaLab/IdeaLabDrawer'

import s from './CRRDrawer.module.css'
import CompetitorLogo from 'container/SEOTools/ContentGapReport/Insights/Card/CompetitorLogo'

type Props = {
  open: boolean
  onClose: () => void
  onCreateOrderClick?: (topic: Topic) => void
  onCreatePrivateDocClick?: (topic: Topic) => void
  secondaryKeywords?: TopicKeyword[]
  topic?: Topic
}

const ERROR_STATUS_ID = 4

function CRRDrawer({
  open,
  topic,
  onClose,
  onCreateOrderClick,
  onCreatePrivateDocClick,
  secondaryKeywords,
}: Props): JSX.Element {
  const companyId = useSelectedCompanyId()

  const { data, isLoading } = useGetReport({
    data: { companyId, reportId: topic?.crr_id },
    options: { enabled: !!topic?.crr_id },
  })

  const getIsOutlineLoading = () => {
    const isGeneratingReport = data
      ? ['processing', 'pending', 'error'].includes(data.generation_status_name)
      : isLoading

    return isGeneratingReport
  }

  const handleOpenInDocs = () => {
    if (!topic?.item_id || !topic.item_type) return

    if (topic.item_type === 'private_doc') {
      window.open(`/${companyId}/items/${topic.item_id}`)
    } else {
      window.open(`/${companyId}/document/${topic.item_id}`)
    }
  }

  const getTabElement = (key: string) => {
    const primaryKeyword = topic?.keywords?.find(
      keyword => keyword.type === 'primary'
    )
    if (!primaryKeyword) return

    switch (key) {
      case 'guidelines':
        return (
          <ContentGuidelines
            reportId={data?.id}
            report={data?.document}
            itemId={topic?.item_id}
            generationStatusId={data?.generation_status_id}
            primaryKeyword={{
              keyword: primaryKeyword.keyword,
              heading_presence: 0,
              min: 0,
              max: 0,
              search_volume: primaryKeyword.volume,
            }}
            secondaryKeywords={secondaryKeywords?.map(secondary => ({
              keyword: secondary.keyword,
              max: 0,
              min: 0,
              heading_presence: 0,
              search_volume: secondary.volume,
            }))}
          />
        )
      case 'outline':
        return (
          <Outline
            ai_outline={data?.document?.ai_outline}
            competitors={data?.document?.competitors}
            isGeneratingReport={getIsOutlineLoading()}
          />
        )
      case 'faq':
        return <FAQs faqs={data?.document?.faqs} />
      default:
        return null
    }
  }

  const handleCreateOrder = () => {
    if (topic && onCreateOrderClick) {
      onCreateOrderClick(topic)
    }
  }

  const handleCreatePrivateDoc = () => {
    if (topic && onCreatePrivateDocClick) {
      onCreatePrivateDocClick(topic)
    }
  }

  useEffect(() => {
    if (data?.generation_status_id === ERROR_STATUS_ID) {
      apm.captureError('Report generation failed')
      apm.addLabels({
        reportId: data.id,
      })
    }
  }, [data])

  return (
    <IdeaLabDrawer
      className={s.drawer}
      title={
        <div className={s.title}>
          <Icon
            icon="heroicons:sparkles-solid"
            width={18}
            height={18}
            className={s.icon}
          />
          <div className={s.header}>
            <div className={s.drawerTitle}>
              <h2>{topic?.name}</h2>
            </div>
            {topic?.item_id ? (
              <Button
                type="primary"
                onClick={handleOpenInDocs}
                icon={<Icon icon="uil:file-alt" />}
                className={s.btnPrimary}
              >
                Open In Docs
              </Button>
            ) : (
              <CreateItemDropdown
                onCreateOrderClick={handleCreateOrder}
                onCreatePrivateDocClick={handleCreatePrivateDoc}
              >
                <Button
                  className={s.btnPrimary}
                  type="primary"
                  icon={<Icon icon="uil:file-alt" />}
                  data-id="seo-guidelines-create-item-button"
                >
                  Create Item
                </Button>
              </CreateItemDropdown>
            )}
          </div>
        </div>
      }
      open={open}
      onClose={onClose}
    >
      <Tabs
        className={s.tabs}
        tabBarExtraContent={
          <div className={s.tabBarExtraContent}>
            <div className={s.tabBarExtraContentText}>
              <Icon icon="lucide:lightbulb" width={16} height={16} />
              <p>Insights picked from top performing pages</p>
            </div>

            <div>
              {data?.document?.competitors.slice(0, 4).map(competitor => {
                const { url } = competitor
                return (
                  <CompetitorLogo
                    key={url}
                    className={s.logoStack}
                    competitor={{ url }}
                  />
                )
              })}
            </div>
          </div>
        }
      >
        {tabs.map(tab => (
          <TabPane
            key={tab.key}
            className={s.tab}
            tab={
              <div className={s.tabTitle}>
                <Icon icon={tab.icon} width="16" height="16" /> {tab.title}
              </div>
            }
          >
            {getTabElement(tab.key)}
          </TabPane>
        ))}
      </Tabs>
    </IdeaLabDrawer>
  )
}

export default CRRDrawer
